import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { PagesService } from '../pages.service';
import { ReviewOrderResponse, AccountType, B2BUnit, ConsignmentReviewRow, AccountListResponse, CartEntryUpdatePayload, RejectDocumentPayload, ConsignmentReviewPayload, ReviewOrderAttachment, SerialValidationEntry, ReviewOrderEntry } from '../pages.models';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { NgxExtendedPdfViewerModule, PagesLoadedEvent } from 'ngx-extended-pdf-viewer';
import { JjsvAccountSelectComponent } from '../../components/jjsv-account-select/jjsv-account-select.component';
import { JjsvLoader } from '../../components/jjsv-loader/jjsv-loader.component';
import { PendingSaveTask, PreActionSaveService } from '../../core/services/pre-action-save.service';
import { environment } from '../../../environments/environment';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
interface ReviewTableRow {
  id: number;
  cartEntryId: any;
  material: string;
  serialNumber: string;
  invoiceText: string;
  deliveryNoteText: string;
  quantity: number;
  orderType: string;
  shipTo: string;
  serialStatus: string;
  userStatus: string;
  isWarning: boolean;
  isShipToConflict: boolean;
  shipToError?: string;
  isNew: boolean;
}

interface LineItemFormControls {
  material: FormControl<string>;
  serialNumber: FormControl<string>;
  quantity: FormControl<number>;
  orderType: FormControl<string>;
}

type LineItemField = 'material' | 'serialNumber' | 'invoiceText' | 'deliveryNoteText' | 'quantity' | 'orderType';
type HeaderTextField = 'purchaseOrder' | 'salesText' | 'deliveryNoteText' | 'comment';
type LineItemTextField = 'invoiceText' | 'deliveryNoteText';

const SERIAL_BLOCKED_ORDER_TYPES = ['OR', 'KB'];
const MATERIAL_BLOCKED_ORDER_TYPES = ['ZKE', 'KE'];
// MIXED is a backend-supplied header state only; it must never be a user-selectable option.
const MIXED_ORDER_TYPE = 'MIXED';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_HEADER_FIELD_LENGTH = 70;
const MAX_PURCHASE_ORDER_LENGTH = 35;
const MAX_SALES_TEXT_LINE_LENGTH = 70;
// Carts in these statuses are locked: the whole page becomes view-only.
const READ_ONLY_CART_STATUSES = ['COMPLETED', 'REJECTED'];
const SHIP_TO_CONFLICT_TITLE = 'Account or status inconsistency found';
const SHIP_TO_CONFLICT_MESSAGE = 'One or more serial numbers in this list are associated with a different account or have a status that requires review. You may continue to submit this request for review';
const SHIP_TO_REQUIRED_MESSAGE = 'Ship-To is required for this line item.';
const SALES_ORG_BY_COUNTRY: Record<string, string> = { FR: '2010', US: '1510' };

function salesTextLineLengthValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '');
  return value.split('\n').some((line) => line.length > MAX_SALES_TEXT_LINE_LENGTH)
    ? { maxlength: true }
    : null;
}

/** Serial lines cannot use OR/KB; material-only lines cannot use ZKE/KE. */
function isOrderTypeBlocked(option: string, hasSerial: boolean): boolean {
  const blocked = hasSerial ? SERIAL_BLOCKED_ORDER_TYPES : MATERIAL_BLOCKED_ORDER_TYPES;
  // Options may arrive as a bare code or with a label (e.g. "OR - Standard Order"), so match whole tokens only.
  return option
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .some((token) => blocked.includes(token));
}

/** Attachment dropdown labels are derived from the file name prefix. */
function labelForAttachment(fileName: string): string {
  const name = (fileName ?? '').toLowerCase();
  if (name.startsWith('marked_')) {
    return 'Marked';
  }
  if (name.startsWith('mail_')) {
    return 'Email';
  }
  return 'Original';
}

interface AccountCard {
  title: string;
  account: string;
  address: string;
  accountType: AccountType;
}

/**
 * Review workspace for a cart that has been staged as a purchase order.
 *
 * Business flow:
 * 1. The cart id is read from the route and the review data, account data,
 *    dropdown options, line items, and document attachments are loaded from SAP.
 * 2. Header-level values are edited through `form`; line-item values are held in
 *    `rows` and mirrored into `lineItemsForm` for the template.
 * 3. Line changes are saved independently when focus leaves the table. Header
 *    values are assembled into the order payload when the user places the order
 *    or leaves the page.
 * 4. A Ship-To change can require a Sold-To choice. The Ship-To is temporarily
 *    kept in `pendingShipTo` until both account choices can be committed together.
 * 5. Serial validation returns status by cart-entry id. Matching rows are marked
 *    with warning/status values, which drives the review banner and row styling.
 *
 * This component intentionally keeps display-only account names/addresses beside
 * the reactive form because the save API needs both ids and descriptive values.
 */
@Component({
  selector: 'app-review-orders-page',
  imports: [CommonModule, ReactiveFormsModule, NgxExtendedPdfViewerModule, JjsvAccountSelectComponent, JjsvLoader],
  templateUrl: './review-orders-page.component.html',
  styleUrl: './review-orders-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:beforeunload)': 'onBeforeUnload()',
    '(window:keydown)': 'onKeyDown($event)'
  }
})
export class ReviewOrdersPage implements OnInit, OnDestroy {

  readonly pagesService = inject(PagesService);
  private readonly preActionSave = inject(PreActionSaveService);
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  private enterNavigationPending = false;
  private activeCartId = '';
  private exitPersistenceRequested = false;
  private exitPersistence$: Observable<boolean> | null = null;
  // True while Place Order / Reject / Send to Consignment Review is executing or has completed.
  private finalActionInProgress = false;
  // True once a browser reload has been requested, so unload must not touch the cart status.
  private reloadInProgress = false;
  // True when the cart is in review by another session; this visit must not touch its status.
  private cartInReviewElsewhere = false;
  private initialOrderPayloadSnapshot: string | null = null;
  // Ship-to/sold-to name & address are display-only (not form controls) but required by the save payload.
  private shipToName = '';
  private shipToAddress = '';
  private soldToName = '';
  private soldToAddress = '';
  private pdfSubscription: Subscription | null = null;
  private lineItemSave$: Observable<unknown> | null = null;
  // Tracks an in-flight header-triggered /updateCartHeader order-type save so dependent actions can wait on it.
  private headerOrderTypeSave$: Observable<unknown> | null = null;
  // Ship-To selected while waiting on the follow-up Sold-To modal (uncommitted until Sold-To is chosen).
  readonly pendingShipTo = signal<B2BUnit | null>(null);
  // Baseline row values from the last loaded/saved backend state, keyed by row id.
  private readonly originalRowsById = new Map<number, ReviewTableRow>();
  private readonly dirtyRowIds = new Set<number>();
  private pendingDeleteRows: ReviewTableRow[] = [];
  readonly pdfDocuments = signal<ReviewOrderAttachment[]>([]);
  readonly isPdfLoading = signal<boolean>(false);
  readonly pdfError = signal<string>('');
  readonly pageTitle = signal<string>('');
  readonly pageSubtitle = signal<string>('');
  readonly mandatoryNote = signal<string>('');
  readonly alertTitle = signal<string>('');
  readonly alertMessage = signal<string>('');
  readonly orderTypeOptions = signal<string[]>([]);
  readonly shippingMethodOptions = signal<string[]>([]);
  readonly incotermOptions = signal<string[]>([]);
  readonly accountCards = signal<AccountCard[]>([]);
  readonly pageSizeOptions = [10, 20, 50];

  readonly showAccountModal = signal(false);
  readonly modalAccountType = signal<AccountType>('SHIP_TO');
  readonly modalAccountData = signal<AccountListResponse | null>(null);
  readonly modalCartId = signal('');
  readonly isSendToSNLoading = signal(false);
  readonly showConsignmentModal = signal(false);
  readonly consignmentRows = signal<ConsignmentReviewRow[]>([]);
  readonly consignmentComment = signal('');
  readonly consignmentEmails = signal('');
  readonly consignmentSubmitted = signal(false);
  readonly isConsignmentSaving = signal(false);
  readonly headerShipTo = signal('');
  readonly orderCountry = signal('');
  readonly displayedRejectionReason = signal('');
  readonly displayedRejectionComment = signal('');
  readonly showRejectionDetails = computed(() => !!this.displayedRejectionReason() || !!this.displayedRejectionComment());

  readonly showRejectModal = signal(false);
  readonly isRejectSaving = signal(false);
  readonly isPlaceOrderSaving = signal(false);
  readonly isDeleteLoading = signal(false);
  readonly rejectSubmitted = signal(false);
  readonly rejectReasonOptions = signal<string[]>([]);
  readonly rejectNotifyOptions = signal<string[]>([]);
  readonly isRejectConfigLoading = signal(false);
  readonly rejectReason = signal('');
  readonly rejectAdditionalDetails = signal('');
  readonly rejectNotifyRecipient = signal('');
  readonly rejectAdditionalEmails = signal('');

  readonly showInlineWarning = signal<boolean>(false);
  readonly alertType = signal<'error' | 'success'>('error');
  readonly accountExpanded = signal<boolean>(true);
  readonly orderDetailsExpanded = signal<boolean>(true);
  readonly textareaExpanded = signal<boolean>(false);

  readonly pdfUrl = signal<string>('');
  readonly pdfSrc = signal<Blob | null>(null);
  readonly pdfPage = signal<number>(1);
  readonly pdfZoom = signal<number>(40);
  readonly pdfRotation = signal<number>(0);
  readonly pdfTotalPages = signal<number>(0);

  readonly hasPdfDocuments = computed(() => this.pdfDocuments().length > 0);
  readonly hasPdfSource = computed(() => this.pdfSrc() !== null);

  readonly pdfDocumentOptions = computed(() =>
    this.pdfDocuments().map(doc => ({
      documentId: doc.documentId,
      label: labelForAttachment(doc.fileName),
    }))
  );

  readonly rows = signal<ReviewTableRow[]>([]);
  readonly pageSize = signal<number>(10);
  readonly currentPage = signal<number>(1);

  readonly totalResults = computed(() => this.rows().length);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalResults() / this.pageSize())));
  readonly hasShipToMismatch = computed(() =>
    this.rows().some((row) => row.shipTo !== this.headerShipTo())
  );
  readonly hasSerialNumber = computed(() =>
    this.rows().some((row) => this.hasRowSerial(row))
  );
  readonly showUsFields = computed(() => this.orderCountry() === 'US');
  readonly showFranceFields = computed(() => this.orderCountry() === 'FR');
  readonly isRejectNotifyRequired = computed(() => this.orderCountry() !== 'FR');

  readonly cartStatus = signal<string>('');
  // Single status-based flag gating every editable control/action on the page.
  readonly isReadOnly = computed(() => READ_ONLY_CART_STATUSES.includes(this.cartStatus().trim().toUpperCase()));
  readonly headerLengthExceeded = signal<Record<HeaderTextField, boolean>>({
    purchaseOrder: false,
    salesText: false,
    deliveryNoteText: false,
    comment: false,
  });
  readonly lineItemLengthExceeded = signal<Record<string, boolean>>({});

  /** Header form. Required controls are checked before order submission. */
  readonly form = new FormGroup({
    orderType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    purchaseOrder: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(MAX_PURCHASE_ORDER_LENGTH)] }),
    shippingMethod: new FormControl('', { nonNullable: true }),
    incoterms: new FormControl('', { nonNullable: true }),
    salesText: new FormControl('', { nonNullable: true, validators: [salesTextLineLengthValidator] }),
    deliveryNoteText: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(MAX_HEADER_FIELD_LENGTH)] }),
    comment: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(MAX_HEADER_FIELD_LENGTH)] }),
    shipToId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    soldToId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  /**
   * Form representation of the table rows. The source of truth remains `rows`;
   * this array is rebuilt without emitting events whenever the row signal changes.
   */
  readonly lineItemsForm = new FormArray<FormGroup<LineItemFormControls>>([]);
  private previousLineItemRowIds: number[] = [];

  readonly pagedRows = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.rows().slice(start, start + this.pageSize());
  });

  /** Header order type options with per-option disabling driven by cart-wide serial-number mix; MIXED is never selectable. */
  readonly headerOrderTypeOptions = computed(() => {
    const options = this.orderTypeOptions();
    const criteriaRows = this.rows().filter((row) => this.hasRowOrderTypeCriteria(row));
    const allSerial = criteriaRows.length > 0 && criteriaRows.every((row) => this.hasRowSerial(row));
    const allMaterialOnly = criteriaRows.length > 0 && criteriaRows.every((row) => !this.hasRowSerial(row));

    return options.map((option) => ({
      value: option,
      disabled:
        option === MIXED_ORDER_TYPE ||
        (allSerial && isOrderTypeBlocked(option, true)) ||
        (allMaterialOnly && isOrderTypeBlocked(option, false)),
    }));
  });

  /** Per-row view state: recomputed whenever rows or the header order type list change. */
  readonly pagedRowViews = computed(() => {
    const options = this.orderTypeOptions().filter((option) => option !== MIXED_ORDER_TYPE);
    return this.pagedRows().map((row) => {
      const hasSerial = this.hasRowSerial(row);
      const hasOrderTypeCriteria = this.hasRowOrderTypeCriteria(row);
      return {
        row,
        quantityDisabled: hasSerial,
        orderTypeOptions: options.map((option) => ({
          value: option,
          disabled: hasOrderTypeCriteria && isOrderTypeBlocked(option, hasSerial),
        })),
      };
    });
  });

  readonly pageTokens = computed<(number | '...')[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();

    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    if (current <= 4) {
      return [1, 2, 3, 4, '...', total];
    }

    if (current >= total - 3) {
      return [1, '...', total - 3, total - 2, total - 1, total];
    }

    return [1, '...', current - 1, current, current + 1, '...', total];
  });

  readonly fromResult = computed(() => {
    if (this.totalResults() === 0) {
      return 0;
    }
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly toResult = computed(() => {
    return Math.min(this.currentPage() * this.pageSize(), this.totalResults());
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: PagesService
  ) {
    // Keep the line-item form separate from the header form while preserving the existing rows state.
    effect(() => {
      this.syncLineItemsForm(this.rows());
    });

    // The page is rendered through the CMS route, so CanDeactivate does not always run.
    // Starting the exit sequence here guarantees saves and updateCartStatus are issued
    // before the destination page requests dashboard data.
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => this.beginExitSequence());

    this.pagesService.updateNavigationNodes({
      link: [
        { label: 'Home', path: '/homepage', isActive: false },
        { label: 'Purchase Orders', path: '/purchase-orders', isActive: true },
      ],
      loginStatus: true,
      subNavLinks: [{ label: 'Review Purchase Order Data', path: '/review-orders', isActive: true }],
    });
  }

  ngOnInit(): void {
    /** The cart id is the key used by every review-page API request. */
    const cartId = this.route.snapshot.queryParams['cartId'] ?? this.route.snapshot.params['cartId'];
    if (cartId) {
      this.activeCartId = cartId;
      this.pagesService.registerActiveReviewCart(cartId);
      //this.pagesService.updateCartStatus(cartId, true).subscribe();
      this.loadReviewOrder(cartId);
    }
  }

  ngOnDestroy(): void {
    // CanDeactivate or beforeunload may already have persisted the exit state.
    // The guard prevents a second request when that has happened.
    if (this.exitPersistenceRequested || this.isStatusUpdateBlocked()) {
      return;
    }

    this.notifyCartLeft();
  }

  onBeforeUnload(): void {
    // A reload or a finalized business action owns the cart status, so nothing may overwrite it.
    if (this.isStatusUpdateBlocked()) {
      return;
    }
    // Browser unload does not reliably wait for normal Observable subscriptions.
    this.sendBeaconCartLeft();
  }

  /** Reload shortcuts are the only reload signal available before the page unloads. */
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key?.toLowerCase();
    if (key === 'f5' || ((event.ctrlKey || event.metaKey) && key === 'r')) {
      this.reloadInProgress = true;
    }
  }

  /** /updateCartStatus must be skipped while a final action or a reload owns the cart status. */
  private isStatusUpdateBlocked(): boolean {
    // The service marker also covers a re-created page instance, where the local flag is lost.
    return this.finalActionInProgress || this.reloadInProgress || this.cartInReviewElsewhere || this.pagesService.isReviewFinalActionCart(this.activeCartId);
  }

  /** Marks the cart as owned by a final business action so no exit handler overwrites its status. */
  private beginFinalAction(): void {
    this.finalActionInProgress = true;
    this.pagesService.setReviewFinalAction(this.activeCartId, true);
  }

  /**
   * Leaves the page after a successful final action. The shared marker is released only once
   * navigation has settled (this instance's exit handlers have already been skipped), so a later
   * visit to the same cart is not blocked from sending /updateCartStatus on exit.
   */
  private navigateAfterFinalAction(redirectTo?: string): void {
    const cartId = this.activeCartId;
    this.router
      .navigate(redirectTo ? [redirectTo] : ['/homepage'])
      .finally(() => this.pagesService.setReviewFinalAction(cartId, false));
  }

  /** Releases the final-action lock when the action could not be completed. */
  private endFinalAction(): void {
    this.finalActionInProgress = false;
    this.pagesService.setReviewFinalAction(this.activeCartId, false);
  }

  private notifyCartLeft(): void {
    this.persistExitState().subscribe();
  }

  /** Runs the exit sequence once per visit and lets dashboard requests queue behind it. */
  private beginExitSequence(): void {
    if (!this.activeCartId || this.exitPersistence$ || this.isStatusUpdateBlocked()) {
      return;
    }

    const exit$ = this.persistExitState();
    this.pagesService.registerReviewExit(exit$);
    exit$.subscribe();
  }

  // Uses sendBeacon so the request completes even when the page is unloading
  private sendBeaconCartLeft(): void {
    if (!this.activeCartId) return;
    this.exitPersistenceRequested = true;
    const hasUnsavedChanges = this.hasUnsavedChanges();

    this.sendBeaconLineItems();

    // Nothing needs to be saved, but the active-cart marker was still cleared above.
    if (hasUnsavedChanges) {
      const detailsUrl = this.pagesService.buildSaveOrderDetailsUrl(this.activeCartId);
      const detailsPayload = JSON.stringify(this.buildOrderPayload());
      navigator.sendBeacon(detailsUrl, new Blob([detailsPayload], { type: 'application/json' }));
   }

    this.pagesService.markActiveReviewCartLeft(true);
  }

  /** Flushes unsaved line items during browser unload, where Observables cannot be awaited. */
  private sendBeaconLineItems(): void {
    const { dirtyRows, deleteRows } = this.takePendingLineItemChanges();
    if (dirtyRows.length === 0 && deleteRows.length === 0) {
      return;
    }

    const entryUrl = this.pagesService.buildUpdateCartEntryUrl();
    const send = (payload: CartEntryUpdatePayload) =>
      navigator.sendBeacon(entryUrl, new Blob([JSON.stringify(payload)], { type: 'application/json' }));

    dirtyRows.forEach((row) => send(this.buildCartEntryPayload(row, false)));
    deleteRows.forEach((row) => send(this.buildCartEntryPayload(row, true)));
  }

  /**
   * Single pre-navigation handler used by the CanDeactivate guard, in-page Back and
   * component teardown. Saves pending changes first, then marks the cart as left, so
   * the backend recalculates status from the latest header and line-item data.
   * The resulting stream is shared so concurrent callers never duplicate the requests.
   */
  persistExitState(): Observable<boolean> {
    if (!this.activeCartId || this.isStatusUpdateBlocked()) {
      return of(true);
    }

    if (this.exitPersistence$) {
      return this.exitPersistence$;
    }
    if (!this.validateLineItems()) {
      this.lineItemsForm.markAllAsTouched();
      this.alertType.set('error');
      this.alertTitle.set('Validation Failed');
      this.alertMessage.set('Please correct the highlighted line-item fields.');
      this.showInlineWarning.set(true);
      //return of(false);
    }
    this.exitPersistenceRequested = true;
    this.exitPersistence$ = this.preActionSave.save(this.exitSaveTasks()).pipe(
      switchMap(() => this.pagesService.persistReviewExit(this.activeCartId)),
      catchError((error) => {
        console.error('Failed to save pending changes before leaving review:', error);
        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.exitPersistence$;
  }

  /** Exit order required by the backend status calculation: header order type, then header, then line items. */
  private exitSaveTasks(): PendingSaveTask[] {
    return [this.headerOrderTypeTask(), this.headerSaveTask(), this.lineItemSaveTask()];
  }

  /**
   * Save tasks evaluated before any in-page user action. Line items are saved first because
   * the header totals returned by the backend depend on the current cart entries.
   */
  private pendingSaveTasks(): PendingSaveTask[] {
    return [this.headerOrderTypeTask(), this.lineItemSaveTask(), this.headerSaveTask()];
  }

  private lineItemSaveTask(): PendingSaveTask {
    return {
      isDirty: () => !!this.lineItemSave$ || this.hasPendingLineItemChanges(),
      save: () => this.saveDirtyRows(),
    };
  }

  private headerSaveTask(): PendingSaveTask {
    return {
      // A missing snapshot means the review data has not loaded yet, so there is nothing to save.
      isDirty: () => !!this.activeCartId && this.initialOrderPayloadSnapshot !== null && this.hasUnsavedChanges(),
      save: () => this.saveHeader(),
    };
  }

  private headerOrderTypeTask(): PendingSaveTask {
    return {
      isDirty: () => !!this.headerOrderTypeSave$,
      save: () => this.afterHeaderOrderTypeSave(),
    };
  }

  /** Awaits a pending header-triggered order-type save so dependent actions never race it. */
  private afterHeaderOrderTypeSave(): Observable<unknown> {
    return this.headerOrderTypeSave$ ? this.headerOrderTypeSave$.pipe(catchError(() => of(null))) : of(null);
  }

  /** Sends the header payload and advances the dirty-check baseline on success. */
  private saveHeader(): Observable<unknown> {
    const payload = this.buildOrderPayload();
    return this.pagesService.saveOrderDetails(payload).pipe(
      tap(() => this.initialOrderPayloadSnapshot = JSON.stringify(payload))
    );
  }

  /**
   * Saves every dirty form and runs `action` only when all saves succeed.
   * Runs the action immediately when nothing is dirty.
   */
  private runAfterSave(action: () => void, onFailure?: () => void, validateLineItems = true): void {
    if (validateLineItems && !this.validateLineItems()) {
      this.lineItemsForm.markAllAsTouched();
      this.alertType.set('error');
      this.alertTitle.set('Validation Failed');
      this.alertMessage.set('Please correct the highlighted line-item fields.');
      this.showInlineWarning.set(true);
      onFailure?.();
      return;
    }
    this.preActionSave.save(this.pendingSaveTasks()).subscribe({
      next: () => action(),
      error: (error) => {
        console.error('Failed to save pending changes before action:', error);
        this.alertType.set('error');
        this.alertTitle.set('Save Failed');
        this.alertMessage.set('Unable to save your changes. Please try again.');
        this.showInlineWarning.set(true);
        onFailure?.();
      },
    });
  }

  /** Builds the order payload from current header form values, matching the updateCartHeader API shape. */
  private buildOrderPayload(): object {
    // Keep this mapping in one place so place-order, normal navigation, and
    // browser-unload persistence send the same header contract to the backend.
    // getRawValue() is used because the order type control can be disabled (e.g. MIXED),
    // and the payload must always reflect the value shown in the UI.
    const v = this.form.getRawValue();
    return {
      cartId: this.activeCartId,
      shippingMethod: v.shippingMethod ?? '',
      purchaseOrder: v.purchaseOrder ?? '',
      orderType: v.orderType ?? '',
      incoterms: v.incoterms ?? '',
      comments: v.comment ?? '',
      shipTo: v.shipToId ?? '',
      shipToAddress: this.shipToAddress,
      shipToName: this.shipToName,
      soldTo: v.soldToId ?? '',
      soldToAddress: this.soldToAddress,
      soldToName: this.soldToName,
      internalSalesText: v.salesText ?? '',
      deliveryNoteText: v.deliveryNoteText ?? '',
    };
  }

  /** True when form/line-item values differ from the values loaded from the API. */
  private hasUnsavedChanges(): boolean {
    if (this.initialOrderPayloadSnapshot === null) {
      return true;
    }
    return JSON.stringify(this.buildOrderPayload()) !== this.initialOrderPayloadSnapshot;
  }

  private loadReviewOrder(cartId: string): void {
    // The response is applied in one pass so the header and line-item baselines
    // represent the same backend snapshot for dirty checking.
    this.pagesService
      .getReviewOrderData(cartId)
      .subscribe({
      next: response => {
        // waiting for backend to send the attribute for redirect or not based on that we need the implimentation so commented for now
          if(!response?.loggedInUser){
            this.cartInReviewElsewhere = true;
            this.pagesService.clearActiveReviewCart(cartId);
            const previousUrl = this.pagesService.getPreviousUrl();
            // Skip self/empty history (direct link or reload) to avoid a redirect loop.
            const canReturn = !!previousUrl && !previousUrl.startsWith('/review-order');
            void this.router.navigateByUrl(canReturn ? previousUrl! : '/homepage');
          }else{
            this.applyApiData(response);
            this.applyReviewAttachments(response?.attachments);
          }
      },
      error: error => {
        console.error('Failed to load review order data:', error);
        this.pdfDocuments.set([]);
        this.clearPdfSource();
        this.isPdfLoading.set(false);
        this.showInlineWarning.set(true);
        this.alertTitle.set('Error Loading Data');
        this.alertMessage.set('Unable to load order details. Please try again.');
      }
    });
  }

  private syncLineItemsForm(rows: ReviewTableRow[]): void {
    const existingGroups = new Map(
      this.previousLineItemRowIds.map((id, index) => [id, this.lineItemsForm.at(index)])
    );
    const nextGroups = rows.map((row) => existingGroups.get(row.id) ?? new FormGroup({
      material: new FormControl(row.material, { nonNullable: true }),
      serialNumber: new FormControl(row.serialNumber, { nonNullable: true }),
      quantity: new FormControl(row.quantity, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1)],
      }),
      orderType: new FormControl(row.orderType, { nonNullable: true, validators: [Validators.required] }),
    }));

    this.lineItemsForm.clear({ emitEvent: false });
    nextGroups.forEach((group) => this.lineItemsForm.push(group, { emitEvent: false }));
    this.previousLineItemRowIds = rows.map((row) => row.id);
    this.validateLineItems();
  }

  private lineItemFormFor(id: number): FormGroup<LineItemFormControls> | undefined {
    const index = this.rows().findIndex((row) => row.id === id);
    return index === -1 ? undefined : this.lineItemsForm.at(index);
  }

  isLineItemFieldInvalid(id: number, field: keyof LineItemFormControls): boolean {
    const control = this.lineItemFormFor(id)?.controls[field];
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  lineItemFieldError(id: number, field: keyof LineItemFormControls): string {
    const control = this.lineItemFormFor(id)?.controls[field];
    if (!control || !(control.touched || control.dirty)) {
      return '';
    }
    if (control.hasError('duplicateSerial')) {
      return 'Duplicate serial number not allowed';
    }
    if (control.hasError('materialOrSerialRequired')) {
      return 'Material or Serial Number is required';
    }
    if (control.hasError('required')) {
      return `${field === 'orderType' ? 'Order type' : field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }
    return '';
  }

  /** Synchronizes row values to the reactive form and applies cart-wide serial uniqueness. */
  private validateLineItems(): boolean {
    const serialCounts = new Map<string, number>();
    this.rows().forEach((row) => {
      const serial = row.serialNumber.trim().toLocaleLowerCase();
      if (serial) {
        serialCounts.set(serial, (serialCounts.get(serial) ?? 0) + 1);
      }
    });

    this.rows().forEach((row) => {
      const group = this.lineItemFormFor(row.id);
      if (!group) {
        return;
      }
      group.controls.material.setValue(row.material, { emitEvent: false });
      group.controls.serialNumber.setValue(row.serialNumber, { emitEvent: false });
      group.controls.quantity.setValue(row.quantity, { emitEvent: false });
      group.controls.orderType.setValue(row.orderType, { emitEvent: false });

      const materialControl = group.controls.material;
      const serialControl = group.controls.serialNumber;
      const hasMaterial = row.material.trim().length > 0;
      const serial = row.serialNumber.trim().toLocaleLowerCase();

      const materialErrors = { ...(materialControl.errors ?? {}) };
      if (!hasMaterial && !serial) {
        materialControl.setErrors({ ...materialErrors, materialOrSerialRequired: true });
      } else if (materialErrors['materialOrSerialRequired']) {
        delete materialErrors['materialOrSerialRequired'];
        materialControl.setErrors(Object.keys(materialErrors).length ? materialErrors : null);
      }

      const errors = { ...(serialControl.errors ?? {}) };
      if (!hasMaterial && !serial) {
        errors['materialOrSerialRequired'] = true;
      } else {
        delete errors['materialOrSerialRequired'];
      }
      if (serial && (serialCounts.get(serial) ?? 0) > 1) {
        errors['duplicateSerial'] = true;
      } else {
        delete errors['duplicateSerial'];
      }
      serialControl.setErrors(Object.keys(errors).length ? errors : null);
    });

    return this.lineItemsForm.valid;
  }

  private markLineItemFieldTouched(id: number, field: keyof LineItemFormControls): void {
    this.lineItemFormFor(id)?.controls[field].markAsTouched();
  }

  /** Opens the account selection modal for the given account type. */
  openAccountModal(accountType: AccountType): void {
    if (this.isReadOnly()) {
      return;
    }
    const cartId = this.route.snapshot.queryParams['cartId'];
    this.modalCartId.set(cartId);
    this.modalAccountData.set(null);
    this.modalAccountType.set(accountType);
    this.pendingShipTo.set(null);
    this.showAccountModal.set(true);
  }

  onAccountModalClose(): void {
    this.showAccountModal.set(false);
    this.pendingShipTo.set(null);
  }

  onAccountSelected(unit: B2BUnit): void {
    if (this.modalAccountType() === 'SHIP_TO') {
      this.selectShipTo(unit);
    } else {
      this.selectSoldTo(unit);
    }
  }

  /** Ship-To selection: commits immediately when Sold-To has a single option, otherwise defers to the Sold-To modal. */
  private selectShipTo(shipTo: B2BUnit): void {
    // The first account call tells us whether this Ship-To has zero, one, or
    // multiple valid Sold-To choices. Only the multi-choice case needs a second
    // modal step; the single-choice case is applied from this same response.
    this.pagesService.updateAccount(shipTo.uid, shipTo.displayName, 'SHIP_TO').subscribe(response => {
      const nextAccountType = response?.accountsType;
      const returnedAccounts = response?.allUnits?.b2BUnits ?? [];

      if (nextAccountType === 'SOLD_TO' && returnedAccounts.length > 1) {
        // Do not commit Ship-To yet; let the Sold-To modal drive the combined update.
        this.pendingShipTo.set(shipTo);
        this.modalAccountData.set({
          accountsType: 'SOLD_TO',
          allUnits: response!.allUnits!,
          salesOrg: response?.salesOrg ?? '',
        });
        this.modalAccountType.set('SOLD_TO');
        return;
      }

      this.applyAccountToState('SHIP_TO', shipTo);
      if (nextAccountType === 'SOLD_TO' && returnedAccounts.length === 1) {
        // Single Sold-To option: auto-select it from the same response (no extra API call).
        this.applyAccountToState('SOLD_TO', returnedAccounts[0]);
      }
      this.showAccountModal.set(false);
    });
  }

  /** Sold-To selection (from the modal opened after Ship-To): commits both accounts together. */
  private selectSoldTo(soldTo: B2BUnit): void {
    // Commit the dependent pair after SAP accepts the Sold-To update. This avoids
    // showing a partially changed account pair while the modal flow is pending.
    const shipTo = this.pendingShipTo();
    this.pagesService.updateAccount(soldTo.uid, soldTo.displayName, 'SOLD_TO').subscribe(() => {
      if (shipTo) {
        this.applyAccountToState('SHIP_TO', shipTo);
        this.pendingShipTo.set(null);
      }
      this.applyAccountToState('SOLD_TO', soldTo);
      this.showAccountModal.set(false);
    });
  }

  /** Reflects the selected account in the account cards and header form/state. */
  private applyAccountToState(accountType: AccountType, unit: B2BUnit): void {
    // Account cards are display state; the form ids and private name/address fields
    // are the values later consumed by buildOrderPayload().
    this.accountCards.update(cards =>
      cards.map(card => {
        if (card.accountType !== accountType) return card;
        return {
          ...card,
          account: [unit.displayCode, unit.displayName].filter(Boolean).join(' - '),
          address: unit.townCity ?? '',
        };
      })
    );

    if (accountType === 'SHIP_TO') {
      this.form.controls.shipToId.setValue(unit.uid);
      this.headerShipTo.set(unit.uid);
      this.shipToName = unit.displayName ?? '';
      this.shipToAddress = unit.townCity ?? '';
    } else {
      this.form.controls.soldToId.setValue(unit.uid);
      this.soldToName = unit.displayName ?? '';
      this.soldToAddress = unit.townCity ?? '';
    }
  }

  /** Toggles the Account section expand/collapse state. */
  toggleAccount(): void {
    this.accountExpanded.update((value) => !value);
  }

  /** Toggles the Order Details form section expand/collapse state. */
  toggleOrderDetails(): void {
    this.orderDetailsExpanded.update((value) => !value);
  }

  /** Toggles the Sales Text textarea between collapsed and expanded height. */
  toggleTextarea(): void {
    this.textareaExpanded.update((value) => !value);
  }

  /**
   * The API's order type value/options can drift in case or format (e.g. a bare code vs a
   * labeled option) from what the dropdown displays, so entries/header values are matched
   * against the known option list rather than compared as raw strings.
   */
  private resolveOrderTypeOption(rawValue: string | undefined | null): string {
    const value = (rawValue ?? '').trim();
    if (!value) {
      return '';
    }

    const options = this.orderTypeOptions();
    const exact = options.find((option) => option === value);
    if (exact) {
      return exact;
    }

    const normalized = value.toUpperCase();
    const match = options.find(
      (option) => option.toUpperCase() === normalized || option.toUpperCase().split(/[^A-Z0-9]+/)[0] === normalized
    );
    return match ?? value;
  }

  /**
   * Recomputes the header order type from the current line items:
   * - No line items: leave the header field as-is (enabled).
   * - One line item: header mirrors that line item's order type.
   * - Multiple line items with the same order type: header mirrors it, stays enabled.
   * - Multiple line items with different order types, or a mix of serial/non-serial
   *   entries: header becomes MIXED and the field is disabled.
   */
  private syncHeaderOrderTypeForLineItems(): void {
    const rows = this.rows();
    const control = this.form.controls.orderType;

    if (!rows || rows.length === 0) {
      control.enable({ emitEvent: false });
      return;
    }

    const orderTypes = rows.map((row) => row.orderType);
    const sameOrderType = orderTypes.every((type) => type === orderTypes[0]);
    const orderTypeMixed = rows.length > 1 && !sameOrderType;

    const criteriaRows = rows.filter((row) => this.hasRowOrderTypeCriteria(row));
    const allSerial = criteriaRows.length > 0 && criteriaRows.every((row) => this.hasRowSerial(row));
    const allMaterialOnly = criteriaRows.length > 0 && criteriaRows.every((row) => !this.hasRowSerial(row));
    const serialMixed = criteriaRows.length > 1 && !allSerial && !allMaterialOnly;

    if (orderTypeMixed || serialMixed) {
      control.setValue(MIXED_ORDER_TYPE, { emitEvent: false });
      control.disable({ emitEvent: false });
      return;
    }

    control.enable({ emitEvent: false });
    control.setValue(rows.length === 1 ? (rows[0].orderType ?? '') : orderTypes[0], { emitEvent: false });
  }

  /**
   * The header order type field only allows values valid for every row (see
   * headerOrderTypeOptions), so a user-driven header change is persisted via
   * /updateCartHeader and the backend propagates it to every line item; the UI mirrors
   * that result without marking rows dirty, so no /updateCartEntry call follows it.
   */
  updateHeaderOrderType(event: Event): void {
    if (this.isReadOnly()) {
      return;
    }
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    const previousValue = this.form.controls.orderType.value;
    const previousRows = this.rows();

    this.form.controls.orderType.setValue(value);
    const payload = this.buildOrderPayload();

    this.headerOrderTypeSave$ = this.pagesService.saveOrderDetails(payload).pipe(
      tap(() => {
        this.initialOrderPayloadSnapshot = JSON.stringify(payload);
        this.rows.set(previousRows.map((row) => ({ ...row, orderType: value })));
        this.syncHeaderOrderTypeForLineItems();
      }),
      catchError((error) => {
        console.error('Failed to update header order type:', error);
        this.form.controls.orderType.setValue(previousValue, { emitEvent: false });
        this.rows.set(previousRows);
        this.alertType.set('error');
        this.alertTitle.set('Update Failed');
        this.alertMessage.set('Unable to update order type. Please try again.');
        this.showInlineWarning.set(true);
        return of(null);
      }),
      tap({ finalize: () => (this.headerOrderTypeSave$ = null) }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.headerOrderTypeSave$.subscribe();
  }

  /** Syncs a named header form control with the value emitted by an input or select event. */
  updateHeaderField(field: 'purchaseOrder' | 'shippingMethod' | 'incoterms' | 'salesText' | 'deliveryNoteText' | 'comment', event: Event): void {
    if (this.isReadOnly()) {
      return;
    }
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.form.controls[field].setValue(value);
    if (this.isHeaderTextField(field)) {
      const exceedsLimit = field === 'salesText'
        ? this.hasSalesTextLineLengthExceeded(value)
        : value.length > this.headerFieldMaxLength(field);
      this.headerLengthExceeded.update((state) => ({ ...state, [field]: exceedsLimit }));
    }
  }

  /** Rejects an insertion beyond the native maxlength so the user gets immediate feedback. */
  onHeaderFieldBeforeInput(field: HeaderTextField, event: InputEvent): void {
    if (this.isReadOnly()) {
      return;
    }
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target || !event.inputType.startsWith('insert')) {
      return;
    }
    const selectedLength = (target.selectionEnd ?? target.value.length) - (target.selectionStart ?? target.value.length);
    if (field === 'salesText') {
      if (event.inputType === 'insertFromPaste') {
        return;
      }
      const selectionStart = target.selectionStart ?? target.value.length;
      const selectionEnd = target.selectionEnd ?? target.value.length;
      const insertedValue = event.inputType === 'insertLineBreak' ? '\n' : event.data ?? '';
      const nextValue = target.value.slice(0, selectionStart) + insertedValue + target.value.slice(selectionEnd);
      if (this.hasSalesTextLineLengthExceeded(nextValue)) {
        event.preventDefault();
        this.headerLengthExceeded.update((state) => ({ ...state, [field]: true }));
      }
      return;
    }

    const maxLength = this.headerFieldMaxLength(field);
    const insertedLength = event.data?.length ?? 0;
    if (target.value.length - selectedLength + insertedLength > maxLength) {
      event.preventDefault();
      this.headerLengthExceeded.update((state) => ({ ...state, [field]: true }));
    }
  }

  /** Truncates pasted sales text per line while preserving all line breaks. */
  onHeaderFieldPaste(field: HeaderTextField, event: ClipboardEvent): void {
    if (this.isReadOnly() || field !== 'salesText') {
      return;
    }
    const target = event.target as HTMLTextAreaElement | null;
    const pastedValue = event.clipboardData?.getData('text/plain');
    if (!target || pastedValue === undefined) {
      return;
    }

    event.preventDefault();
    const pastedLines = pastedValue.split(/\r\n?|\n/);
    const normalizedLines = pastedLines.map((line) => line.slice(0, MAX_SALES_TEXT_LINE_LENGTH));
    const normalizedValue = normalizedLines.join('\n');
    const selectionStart = target.selectionStart ?? target.value.length;
    const selectionEnd = target.selectionEnd ?? target.value.length;
    const nextValue = target.value.slice(0, selectionStart) + normalizedValue + target.value.slice(selectionEnd);
    target.value = nextValue;
    target.setSelectionRange(selectionStart + normalizedValue.length, selectionStart + normalizedValue.length);
    target.dispatchEvent(new Event('input', { bubbles: true }));
    this.headerLengthExceeded.update((state) => ({
      ...state,
      [field]: normalizedLines.some((line, index) => line.length < pastedLines[index].length),
    }));
  }

  private hasSalesTextLineLengthExceeded(value: string): boolean {
    return value.split('\n').some((line) => line.length > MAX_SALES_TEXT_LINE_LENGTH);
  }

  private headerFieldMaxLength(field: HeaderTextField): number {
    if (field === 'purchaseOrder') {
      return MAX_PURCHASE_ORDER_LENGTH;
    }
    return MAX_HEADER_FIELD_LENGTH;
  }

  isHeaderTextField(field: string): field is HeaderTextField {
    return ['purchaseOrder', 'salesText', 'deliveryNoteText', 'comment'].includes(field);
  }

  /** Updates a text field (including material and serialNumber) on the row identified by id. */
  updateRowText(id: number, field: 'invoiceText' | 'deliveryNoteText' | 'orderType' | 'material' | 'serialNumber', event: Event): void {
    if (this.isReadOnly()) {
      return;
    }
    // Material and serial input is normalized before state changes so the value
    // displayed in the control is exactly the value that will be sent to SAP.
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    let value = target?.value ?? '';

    if (field === 'material' || field === 'serialNumber') {
      value = this.sanitizeMaterialSerialInput(value);
      if (target) {
        target.value = value;
      }
    }
    if (this.isLineItemTextField(field)) {
      const key = this.lineItemLengthKey(id, field);
      this.lineItemLengthExceeded.update((state) => ({ ...state, [key]: value.length > MAX_HEADER_FIELD_LENGTH }));
    }

    this.rows.update((items) =>
      items.map((row) => {
        if (row.id !== id) {
          return row;
        }

        const updated = { ...row, [field]: value } as ReviewTableRow;
        if (field === 'serialNumber') {
          // Stale SAP warning is cleared on edit; Refresh re-validates.
          updated.isWarning = false;
        }
        if (field === 'serialNumber' || field === 'material') {
          const hasSerial = this.hasRowSerial(updated);
          // Serial-tracked lines are always single quantity and cannot keep a serial-blocked order type.
          if (hasSerial) {
            updated.quantity = 1;
          }
          if (updated.orderType && this.hasRowOrderTypeCriteria(updated) && isOrderTypeBlocked(updated.orderType, hasSerial)) {
            updated.orderType = '';
          }
        }
        return updated;
      })
    );
    if (field === 'serialNumber') {
      this.lineItemFormFor(id)?.controls.serialNumber.setErrors(null);
    }
    if (field === 'material' || field === 'serialNumber' || field === 'orderType') {
      this.markLineItemFieldTouched(id, field);
      this.validateLineItems();
    }
    this.syncHeaderOrderTypeForLineItems();
    this.evaluateRowDirty(id);
  }

  /** Rejects line-item text insertions beyond the 70-character limit. */
  onLineItemTextBeforeInput(id: number, field: LineItemTextField, event: InputEvent): void {
    if (this.isReadOnly()) {
      return;
    }
    const target = event.target as HTMLInputElement | null;
    if (!target || !event.inputType.startsWith('insert')) {
      return;
    }
    const selectedLength = (target.selectionEnd ?? target.value.length) - (target.selectionStart ?? target.value.length);
    const insertedLength = event.data?.length ?? 0;
    if (target.value.length - selectedLength + insertedLength > MAX_HEADER_FIELD_LENGTH) {
      event.preventDefault();
      const key = this.lineItemLengthKey(id, field);
      this.lineItemLengthExceeded.update((state) => ({ ...state, [key]: true }));
    }
  }

  isLineItemTextField(field: string): field is LineItemTextField {
    return field === 'invoiceText' || field === 'deliveryNoteText';
  }

  lineItemLengthKey(id: number, field: LineItemTextField): string {
    return `${id}:${field}`;
  }

  /** True when the row carries a serial number. */
  hasRowSerial(row: ReviewTableRow): boolean {
    return (row.serialNumber ?? '').trim().length > 0;
  }

  /** Order Type rules apply after the user provides a Material or Serial Number. */
  private hasRowOrderTypeCriteria(row: ReviewTableRow): boolean {
    return (row.material ?? '').trim().length > 0 || this.hasRowSerial(row);
  }

  /** Strips spaces and disallowed characters, allowing only letters, digits and "-" for Material/Serial Number fields. */
  private sanitizeMaterialSerialInput(value: string): string {
    return value.replace(/\s+/g, '').replace(/[^a-zA-Z0-9-]/g, '');
  }

  /** Updates quantity for a row, clamping the value to a minimum of 1. */
  updateRowQuantity(id: number, event: Event): void {
    if (this.isReadOnly()) {
      return;
    }
    const value = Number((event.target as HTMLInputElement | null)?.value ?? 1);
    const quantity = Number.isFinite(value) && value > 0 ? value : 1;
    this.rows.update((items) =>
      items.map((row) => (row.id === id ? { ...row, quantity: this.hasRowSerial(row) ? 1 : quantity } : row))
    );
    this.markLineItemFieldTouched(id, 'quantity');
    this.validateLineItems();
    this.evaluateRowDirty(id);
  }

  /** Changes the rows-per-page setting and resets the table to page 1. */
  updatePageSize(event: Event): void {
    const size = Number((event.target as HTMLSelectElement | null)?.value ?? 10);
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  /** Navigates to the given page number; silently ignores out-of-range values. */
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }

    this.currentPage.set(page);
  }

  /** Navigates to the previous page of the review table. */
  previousPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  /** Navigates to the next page of the review table. */
  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  /** Removes a row by id and adjusts currentPage if it would exceed the new total. */
  deleteRow(id: number): void {
    if (this.isReadOnly() || this.isDeleteLoading()) {
      return;
    }
    this.isDeleteLoading.set(true);
    // Wait for a pending header-order-type save so the delete acts on the backend's latest row state.
    this.afterHeaderOrderTypeSave().pipe(
        switchMap(() => {
          const row = this.rows().find((item) => item.id === id);
          // Only rows already persisted on the backend need a delete=true update; unsaved new rows can just be dropped.
          if (row && !row.isNew) {
            this.pendingDeleteRows.push(row);
          }
          this.dirtyRowIds.delete(id);

          const updated = this.rows().filter((item) => item.id !== id);
          this.rows.set(updated);
          const newTotalPages = Math.max(1, Math.ceil(updated.length / this.pageSize()));
          if (this.currentPage() > newTotalPages) {
            this.currentPage.set(newTotalPages);
          }
          this.syncHeaderOrderTypeForLineItems();
          return this.saveDirtyRows();
        }),
        finalize(() => this.isDeleteLoading.set(false))
    ).subscribe({
        error: (error) => console.error('Failed to delete line item:', error),
    });
  }

  hasHeaderAccounts(): boolean {
    return !!this.form.controls.shipToId.value?.trim() && !!this.form.controls.soldToId.value?.trim();
  }

  addItem(): boolean {
    if (this.isReadOnly()) {
      return false;
    }
    if (!this.hasHeaderAccounts()) {
      this.alertType.set('error');
      this.alertTitle.set('Please select Ship-To and Sold-To before adding an item.');
      this.alertMessage.set('');
      this.showInlineWarning.set(true);
      window.scrollTo(0, 0);
      return false;
    }
    this.rows.update((items) => [...items, this.createBlankRow()]);

    this.currentPage.set(this.totalPages());
    this.syncHeaderOrderTypeForLineItems();
    return true;
  }

  /** Enter moves to the same field in the next row; on the last row it adds a new row first. */
  onRowFieldEnter(id: number, field: LineItemField, event: Event): void {
    event.preventDefault();
    if (this.isReadOnly() || this.enterNavigationPending) {
      return;
    }

    const index = this.rows().findIndex((row) => row.id === id);
    if (index === -1) {
      return;
    }

    // Existing focus-out save behaviour, triggered in parallel with the navigation.
    this.flushDirtyRows();

    const isLastRow = index === this.rows().length - 1;
    if (isLastRow) {
      if (!this.addItem()) {
        return;
      }
    } else {
      this.currentPage.set(Math.floor((index + 1) / this.pageSize()) + 1);
    }
    this.focusRowField(this.rows()[index + 1].id, field);
  }

  private createBlankRow(): ReviewTableRow {
    const nextId = this.rows().reduce((maxId, row) => Math.max(maxId, row.id), 0) + 1;
    return {
      id: nextId,
      cartEntryId: -1,
      material: '',
      serialNumber: '',
      invoiceText: '',
      deliveryNoteText: '',
      quantity: 1,
      orderType: '',
      shipTo: this.headerShipTo(),
      serialStatus: '',
      userStatus: '',
      isWarning: false,
      isShipToConflict: false,
      isNew: true,
    };
  }

  private focusRowField(id: number, field: LineItemField): void {
    // Guard blocks repeated Enter until the target row is rendered and focused.
    this.enterNavigationPending = true;
    afterNextRender(() => {
      document.querySelector<HTMLElement>(`[data-row-field="${id}:${field}"]`)?.focus();
      this.enterNavigationPending = false;
    }, { injector: this.injector });
    this.cdr.markForCheck();
  }

  /** Marks a row dirty only when its current values differ from the original backend data. */
  private evaluateRowDirty(id: number): void {
    // Dirty tracking is deliberately per row: a focus-out event can save only the
    // rows changed since the last successful backend update.
    const current = this.rows().find((row) => row.id === id);
    if (!current) {
      return;
    }

    if (current.isNew) {
      this.dirtyRowIds.add(id);
      return;
    }

    const original = this.originalRowsById.get(id);
    const changed =
      !original ||
      original.material !== current.material ||
      original.serialNumber !== current.serialNumber ||
      original.invoiceText !== current.invoiceText ||
      original.deliveryNoteText !== current.deliveryNoteText ||
      original.quantity !== current.quantity ||
      original.orderType !== current.orderType;

    if (changed) {
      this.dirtyRowIds.add(id);
    } else {
      this.dirtyRowIds.delete(id);
    }
  }

  /** Builds the updateCartEntry payload for a single row. */
  private buildCartEntryPayload(row: ReviewTableRow, isDelete: boolean): CartEntryUpdatePayload {
    // New rows use -1 because they do not have a backend cart-entry id yet.
    return {
      cartId: this.activeCartId,
      cartEntryId: row.isNew ? -1 : row.cartEntryId,
      materialNumber: row.material,
      serialNumber: row.serialNumber,
      quantity: row.quantity,
      orderType: row.orderType,
      inventoryText: row.invoiceText,
      deliveryText: row.deliveryNoteText,
      delete: isDelete,
      shipTo: row.shipTo,
    };
  }

  /**
   * After a newly created row's /updateCartEntry call succeeds, the backend returns the
   * assigned cartEntryId (either directly, or inside an `entries`/`entry` review-response
   * shape). The row is synced in place so its temporary id (-1) is replaced with the real
   * backend id and subsequent deletes/updates target the correct entry - no page refresh needed.
   */
  private applyCreatedEntryToRow(row: ReviewTableRow, response: any): void {
    if (!row.isNew) {
      return;
    }

    const candidates: ReviewOrderEntry[] = Array.isArray(response?.entries)
      ? response.entries
      : response?.entry
        ? [response.entry]
        : response?.cartEntryId != null
          ? [response]
          : [];

    const existingIds = new Set(
      this.rows()
        .filter((item) => item.id !== row.id)
        .map((item) => String(item.cartEntryId))
    );

    const match = candidates.find(
      (entry) =>
        entry?.cartEntryId != null &&
        String(entry.cartEntryId) !== '-1' &&
        !existingIds.has(String(entry.cartEntryId)) &&
        (entry.materialNumber ?? '') === row.material &&
        (entry.serialNumber ?? '') === row.serialNumber
    );

    if (!match) {
      return;
    }

    this.rows.update((items) =>
      items.map((item) => (item.id === row.id ? { ...item, cartEntryId: match.cartEntryId, isNew: false } : item))
    );
    const syncedRow = this.rows().find((item) => item.id === row.id);
    if (syncedRow) {
      this.originalRowsById.set(row.id, { ...syncedRow });
    }
  }

  /** Called when focus leaves a row (or the table); batches all dirty/deleted rows into one save request. */
  onRowFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && currentTarget.contains(nextTarget)) {
      return;
    }
    this.flushDirtyRows();
  }

  /** Sends each dirty/deleted row to the backend as an individual request. */
  private flushDirtyRows(): void {
    this.saveDirtyRows().subscribe({
      error: (error) => console.error('Failed to save line items:', error),
    });
  }

  /** True when at least one line item was edited, added, or deleted since the last save. */
  private hasPendingLineItemChanges(): boolean {
    return !!this.activeCartId && (this.dirtyRowIds.size > 0 || this.pendingDeleteRows.length > 0);
  }

  /**
   * Removes the pending line-item changes from the queues and returns them.
   * Clearing up front prevents repeated focus/action events from sending the same row twice.
   */
  private takePendingLineItemChanges(): { dirtyRows: ReviewTableRow[]; deleteRows: ReviewTableRow[] } {
    const dirtyRows = Array.from(this.dirtyRowIds)
      .map((id) => this.rows().find((row) => row.id === id))
      .filter((row): row is ReviewTableRow => !!row);
    const deleteRows = this.pendingDeleteRows;

    this.dirtyRowIds.clear();
    this.pendingDeleteRows = [];

    return { dirtyRows, deleteRows };
  }

  /** True when a row's own mandatory fields (material/serial, quantity) are satisfied, independent of other rows. */
  private isRowFormValid(id: number): boolean {
    const group = this.lineItemFormFor(id);
    return !group || group.valid;
  }

  /**
   * Saves dirty/deleted rows and completes once every request has finished.
   * Rows that still fail their own mandatory validation are excluded and stay
   * queued (re-added to dirtyRowIds) so a validation error on one row never
   * blocks the backend save of another row that is already complete.
   */
  private saveDirtyRows(): Observable<unknown> {
    if (this.lineItemSave$) {
      return this.lineItemSave$;
    }
    if (!this.hasPendingLineItemChanges()) {
      return of(null);
    }

    // Refreshes per-row validation state/messages without gating the save on the whole form.
    this.validateLineItems();

    const { dirtyRows, deleteRows } = this.takePendingLineItemChanges();
    const savableRows = dirtyRows.filter((row) => this.isRowFormValid(row.id));
    const blockedRows = dirtyRows.filter((row) => !this.isRowFormValid(row.id));

    // Kept pending until the user corrects the mandatory fields; retried on the next flush.
    blockedRows.forEach((row) => {
      this.dirtyRowIds.add(row.id);
      // Touch every control in the row so its mandatory-field errors (e.g. untouched Order Type) render now.
      this.lineItemFormFor(row.id)?.markAllAsTouched();
    });

    if (savableRows.length === 0 && deleteRows.length === 0) {
      return of(null);
    }

    const requests = [
      ...savableRows.map((row) =>
        this.pagesService.updateCartEntries(this.buildCartEntryPayload(row, false)).pipe(
          tap((response) => {
            // New rows must be synced with the backend cartEntryId before their original
            // snapshot is captured, otherwise later deletes still send the stale -1 id.
            this.applyCreatedEntryToRow(row, response);
            const savedRow = this.rows().find((item) => item.id === row.id) ?? row;
            this.originalRowsById.set(row.id, { ...savedRow });
          })
        )
      ),
      ...deleteRows.map((row) => this.pagesService.updateCartEntries(this.buildCartEntryPayload(row, true))),
    ];

    this.lineItemSave$ = forkJoin(requests).pipe(
      catchError((error) => {
        savableRows.forEach((row) => this.dirtyRowIds.add(row.id));
        this.pendingDeleteRows = [...deleteRows, ...this.pendingDeleteRows];
        throw error;
      }),
      tap({ finalize: () => this.lineItemSave$ = null }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.lineItemSave$;
  }

  /** Re-applies the latest cached API response, or falls back to static defaults when no data is stored. */
  refresh(): void {
    if (!this.hasSerialNumber() || this.isReadOnly()) {
      return;
    }

    this.runAfterSave(() => this.validateSerialNumber());
  }

  /** Opens the reject-document modal; pending edits are saved only when the modal is submitted. */
  reject(): void {
    if (this.isReadOnly() || this.isRejectConfigLoading()) {
      return;
    }
    const salesOrg = SALES_ORG_BY_COUNTRY[this.orderCountry()];
    if (!salesOrg) {
      this.showRejectConfigError();
      return;
    }

    this.isRejectConfigLoading.set(true);
    this.pagesService.getRejectModalConfig(salesOrg).subscribe({
      next: (config) => {
        this.isRejectConfigLoading.set(false);
        if (!config.reasons.length) {
          this.showRejectConfigError();
          return;
        }
        this.rejectReasonOptions.set(config.reasons);
        this.rejectNotifyOptions.set(config.emails);
        this.openRejectModal();
      },
      error: () => {
        this.isRejectConfigLoading.set(false);
        this.showRejectConfigError();
      },
    });
  }

  private showRejectConfigError(): void {
    this.alertType.set('error');
    this.alertTitle.set('Reject Configuration Unavailable');
    this.alertMessage.set('Unable to load reject options. Please try again.');
    this.showInlineWarning.set(true);
    window.scrollTo(0, 0);
  }

  private openRejectModal(): void {
    this.rejectSubmitted.set(false);
    this.rejectReason.set('');
    this.rejectAdditionalDetails.set('');
    this.rejectNotifyRecipient.set('');
    this.rejectAdditionalEmails.set('');
    this.showRejectModal.set(true);
  }

  closeRejectModal(): void {
    this.showRejectModal.set(false);
  }

  updateRejectReason(event: Event): void {
    this.rejectSubmitted.set(false);
    this.rejectReason.set((event.target as HTMLSelectElement | null)?.value ?? '');
  }

  updateRejectAdditionalDetails(event: Event): void {
    this.rejectAdditionalDetails.set((event.target as HTMLInputElement | null)?.value ?? '');
  }

  updateRejectNotifyRecipient(event: Event): void {
    this.rejectSubmitted.set(false);
    this.rejectNotifyRecipient.set((event.target as HTMLSelectElement | null)?.value ?? '');
  }

  updateRejectAdditionalEmails(event: Event): void {
    this.rejectSubmitted.set(false);
    this.rejectAdditionalEmails.set((event.target as HTMLInputElement | null)?.value ?? '');
  }

  /** True when any comma-separated additional email fails basic email format validation. */
  isRejectEmailsInvalid(): boolean {
    return this.rejectSubmitted() && !this.areRejectEmailsValid();
  }

  private areRejectEmailsValid(): boolean {
    return this.splitEmails(this.rejectAdditionalEmails()).every((email) => EMAIL_PATTERN.test(email));
  }

  private splitEmails(value: string): string[] {
    return value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  }

  /** Validates required fields and sends the rejection details + cart id to the backend. */
  saveRejectDocument(): void {
    if (this.isReadOnly()) {
      return;
    }
    this.rejectSubmitted.set(true);
    if (!this.rejectReason() || (this.isRejectNotifyRequired() && !this.rejectNotifyRecipient()) || !this.areRejectEmailsValid()) {
      return;
    }

    const payload: RejectDocumentPayload = {
      cartId: this.activeCartId,
      reason: this.rejectReason(),
      additionalDetails: this.rejectAdditionalDetails(),
      email: [this.rejectNotifyRecipient(), ...this.splitEmails(this.rejectAdditionalEmails())].join(', '),
    };

    this.isRejectSaving.set(true);
    this.beginFinalAction();
    this.runAfterSave(
      () => this.submitRejectDocument(payload),
      () => {
        this.isRejectSaving.set(false);
        this.endFinalAction();
      },
      false
    );
  }

  private submitRejectDocument(payload: RejectDocumentPayload): void {
    this.pagesService.rejectDocument(payload).subscribe({
      next: response => {
        this.isRejectSaving.set(false);
        this.closeRejectModal();
        this.notificationService.setMessage({
          title: response?.title ?? 'Document rejected',
          message: response?.message ?? 'This document has been rejected and will not move forward in the process.',
          notificationStatus: response?.notificationType ?? 'INFO',
        });
        this.navigateAfterFinalAction(response?.redirectTo);
      },
      error: error => {
        console.error('Failed to reject document:', error);
        this.isRejectSaving.set(false);
        this.endFinalAction();
        this.closeRejectModal();
        this.alertType.set('error');
        this.alertTitle.set('Reject Document Failed');
        this.alertMessage.set('Unable to reject the document. Please try again.');
        this.showInlineWarning.set(true);
        window.scrollTo(0, 0);
      },
    });
  }

  /** Opens the consignment review modal with line items that mismatch the header ship-to. */
  sendToSN(): void {
    if (this.isReadOnly()) {
      return;
    }
    this.openConsignmentModal();
  }

  private openConsignmentModal(): void {
    const rows = this.rows()
    .filter((row) => {
      const hasMaterial = !!row.material?.trim();
      const hasSerial = !!row.serialNumber?.trim();
      return (
        row.shipTo !== this.headerShipTo() &&
        (hasMaterial || hasSerial)
      );
    })
    .map((row) => ({
      material: row.material,
      serialNumber: row.serialNumber,
      shipto: row.shipTo,
      transferToShipTo: this.headerShipTo(),
    }));

    this.consignmentRows.set(rows);
    this.consignmentComment.set('');
    this.consignmentEmails.set('');
    this.consignmentSubmitted.set(false);
    this.showConsignmentModal.set(true);
  }

  validateSerialNumber(): void {
    // Validation is requested for the whole cart; the response is correlated back
    // to individual rows by cartEntryId rather than by table position.
    this.isSendToSNLoading.set(true);
    this.pagesService.serialNumberValidation(this.activeCartId).subscribe({
      next: response => {
        this.applySerialValidationResult(response ?? []);
        // Refresh validation can return a corrected material number. Persist it
        // through the same dirty-row entry update used by normal edits.
        this.flushDirtyRows();
        this.isSendToSNLoading.set(false);
      },
      error: error => {
        console.error('Failed to validate call to SAP:', error);
        this.isSendToSNLoading.set(false);
      },
    });
  }

  /** Flags rows whose serial number came back invalid and shows an error/success banner accordingly. */
  private applySerialValidationResult(entries: SerialValidationEntry[]): boolean {
    // Missing entries are treated as invalid because an unvalidated serial must
    // not appear successful simply because SAP omitted its result.
    const entriesByCartEntryId = new Map(
      entries.map((entry) => [String(entry.cartEntryId), entry])
    );

    let hasInvalid = false;
    this.rows.update((items) =>
      items.map((row) => {
        if (!this.hasRowSerial(row)) {
          return { ...row, isWarning: false };
        }

        const entry = entriesByCartEntryId.get(String(row.cartEntryId));
        if (!entry || entry.valid !== true) {
          hasInvalid = true;
        }
        if (!entry) {
          return { ...row, isWarning: true };
        }

        return {
          ...row,
          material: entry.materialNumber || row.material,
          isWarning: entry.valid !== true,
          isShipToConflict: false,
          shipToError: (entry.shipTo ?? '').trim().length > 0 ? undefined : row.shipToError,
          shipTo: entry.shipTo,
          serialStatus: entry.serialSystemStatus,
          userStatus: entry.serialUserStatus,
        };
      })
    );

    this.rows().forEach((row) => {
      const entry = entriesByCartEntryId.get(String(row.cartEntryId));
      if (entry?.materialNumber && entry.materialNumber !== this.originalRowsById.get(row.id)?.material) {
        this.evaluateRowDirty(row.id);
      }
    });

    if (hasInvalid) {
      this.alertType.set('error');
      this.alertTitle.set('Invalid Serial Numbers Found');
      this.alertMessage.set('One or more line items contain invalid serial numbers. Please check the highlighted rows and update the serial numbers');
      window.scrollTo(0, 0);
    } else {
      this.alertType.set('success');
      this.alertTitle.set('Serial Number Validation Successful');
      this.alertMessage.set('All line items passed serial number validation.');
      window.scrollTo(0, 0);
    }
    this.showInlineWarning.set(true);
    return hasInvalid;
  }

   /** Marks rows whose validated Ship-To differs from the header Ship-To and blocks place order. */
  private applyShipToConflictResult(): boolean {
    const headerShipTo = this.headerShipTo();
    let hasConflict = false;
    let hasMissingShipTo = false;

    this.rows.update((items) =>
      items.map((row) => {
        const hasShipTo = (row.shipTo ?? '').trim().length > 0;
        const isShipToConflict = hasShipTo && row.shipTo !== headerShipTo;
        if (isShipToConflict) {
          hasConflict = true;
        }
        if (!hasShipTo) {
          hasMissingShipTo = true;
        }
        return { ...row, isShipToConflict, shipToError: hasShipTo ? undefined : SHIP_TO_REQUIRED_MESSAGE };
      })
    );

    if (hasMissingShipTo) {
      this.alertType.set('error');
      this.alertTitle.set('Ship-To Required');
      this.alertMessage.set(SHIP_TO_REQUIRED_MESSAGE);
      this.showInlineWarning.set(true);
      window.scrollTo(0, 0);
      return true;
    }

    if (hasConflict) {
      this.alertType.set('error');
      this.alertTitle.set(SHIP_TO_CONFLICT_TITLE);
      this.alertMessage.set(SHIP_TO_CONFLICT_MESSAGE);
      this.showInlineWarning.set(true);
      window.scrollTo(0, 0);
    }

    return hasConflict;
  }

  closeConsignmentModal(): void {
    this.showConsignmentModal.set(false);
  }

  updateConsignmentComment(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.consignmentComment.set(value);
    this.consignmentSubmitted.set(false);
  }

  updateConsignmentEmails(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.consignmentEmails.set(value);
    this.consignmentSubmitted.set(false);
  }

  /** True when the comment field is empty; comment is mandatory before submitting. */
  isConsignmentCommentInvalid(): boolean {
    return this.consignmentSubmitted() && !this.consignmentComment().trim();
  }

  /** True when any comma-separated additional email fails basic email format validation. */
  isConsignmentEmailsInvalid(): boolean {
    return this.consignmentSubmitted() && !this.areConsignmentEmailsValid();
  }

  private areConsignmentEmailsValid(): boolean {
    return this.splitEmails(this.consignmentEmails()).every((email) => EMAIL_PATTERN.test(email));
  }

  saveConsignmentReview(): void {
    if (this.isReadOnly()) {
      return;
    }
    this.consignmentSubmitted.set(true);
    if (!this.consignmentComment().trim() || !this.areConsignmentEmailsValid()) {
      return;
    }

    const payload: ConsignmentReviewPayload = {
      cartId: this.activeCartId,
      rows: this.consignmentRows(),
      comment: this.consignmentComment(),
      additionalEmails: this.consignmentEmails(),
    };

    this.isConsignmentSaving.set(true);
    this.beginFinalAction();
    this.runAfterSave(
      () => this.submitConsignmentReview(payload),
      () => {
        this.isConsignmentSaving.set(false);
        this.endFinalAction();
      },
      false
    );
  }

  private submitConsignmentReview(payload: ConsignmentReviewPayload): void {
    this.pagesService.saveConsignmentReview(payload).subscribe({
      next: (response) => {
        this.isConsignmentSaving.set(false);
        this.closeConsignmentModal();
        this.notificationService.setMessage({
          title: response?.title ?? 'Document sent for consignment review.',
          message: response?.message ?? 'The serial numbers will be reviewed by the consignment team.',
          notificationStatus: response?.notificationType ?? 'INFO',
        });
        this.navigateAfterFinalAction(response?.redirectTo);
      },
      error: (error) => {
        console.error('Failed to save consignment review:', error);
        this.isConsignmentSaving.set(false);
        this.endFinalAction();
        this.alertType.set('error');
        this.alertTitle.set('Consignment Review Failed');
        this.alertMessage.set('Unable to submit the consignment review. Please try again.');
        this.showInlineWarning.set(true);
        this.closeConsignmentModal();
        window.scrollTo(0, 0);
      },
    });
  }
  /** Validates the header form; shows field errors and the warning banner if invalid. */
  placeOrder(): void {
    // Pending header/line-item edits are persisted first so the submitted cart
    // matches exactly what the user sees on screen.
    if (!this.form.controls['shipToId'].value?.trim() || !this.form.controls['soldToId'].value?.trim()) {
      this.alertType.set('error');
      this.alertTitle.set('Missing Required Information');
      this.alertMessage.set( 'Sold To and Ship To are required before placing the order.' );
      this.showInlineWarning.set(true);
      this.form.markAllAsTouched();
      window.scrollTo(0, 0);
      return;
    }else{
      this.showInlineWarning.set(false);
      this.alertType.set(null);
      this.alertTitle.set('');
      this.alertMessage.set('');
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      window.scrollTo(0, 0);
      return;
    }

    this.isPlaceOrderSaving.set(true);
    this.beginFinalAction();
    // A pending header-order-type save must land before serial validation reads the cart.
    this.afterHeaderOrderTypeSave().subscribe(() => this.validateSerialThenPlaceOrder());
  }

  private validateSerialThenPlaceOrder(): void {
    // Falls back to the active cart id because the routed url carries it as a path param.
    const cartId = this.route.snapshot.queryParams['cartId'] ?? this.activeCartId;
    if (!this.hasSerialNumber()) {
      this.runAfterSave(
        () => this.submitOrder(cartId),
        () => {
          this.isPlaceOrderSaving.set(false);
          this.endFinalAction();
        }
      );
      return;
    }
    // Serial numbers must be validated before any header/line-item save or the place-order call.
    this.pagesService.serialNumberValidation(cartId).subscribe({
      next: response => {
        const hasInvalid = this.applySerialValidationResult(response ?? []);
        if (hasInvalid) {
          this.isPlaceOrderSaving.set(false);
          this.endFinalAction();
          window.scrollTo(0, 0);
          return;
        }
        const hasShipToConflict = this.applyShipToConflictResult();
        if (hasShipToConflict) {
          this.isPlaceOrderSaving.set(false);
          this.endFinalAction();
          return;
        }
        this.runAfterSave(
          () => this.submitOrder(cartId),
          () => {
            this.isPlaceOrderSaving.set(false);
            this.endFinalAction();
          }
        );
      },
      error: error => {
        console.error('Failed to validate serial numbers before placing order:', error);
        this.isPlaceOrderSaving.set(false);
        this.endFinalAction();
        this.alertType.set('error');
        this.alertTitle.set('Invalid Serial Numbers Found');
        this.alertMessage.set('Unable to validate serial numbers. Please try again.');
        this.showInlineWarning.set(true);
        window.scrollTo(0, 0);
      },
    });
  }

  private submitOrder(cartId: string): void {
    this.isPlaceOrderSaving.set(true);
    this.pagesService.placeOrder(cartId).subscribe({
      next: response => {
        this.isPlaceOrderSaving.set(false);
        this.notificationService.setMessage({
          title: response?.title ?? 'Order Placed',
          message: response?.message ?? 'The order has been submitted successfully.',
          notificationStatus: response?.notificationType ?? 'SUCCESS',
        });
        this.navigateAfterFinalAction(response?.redirectTo);
      },
      error: error => {
        console.error('Failed to place order:', error);
        this.isPlaceOrderSaving.set(false);
        this.endFinalAction();
        this.alertType.set('error');
        this.showInlineWarning.set(true);
        this.alertTitle.set('Order Submission Failed');
        this.alertMessage.set('Unable to submit the order. Please try again.');
        window.scrollTo(0, 0);
      },
    });
  }

  /** Navigates to the previous browser history entry. */
  goBack(): void {
    // The CanDeactivate guard runs persistExitState(), so the saves and status update
    // happen through the single pre-navigation handler.
    history.back();
  }

  /** Maps the full API response onto all signals, dropdowns, form controls, and table rows. */
  private applyApiData(data: ReviewOrderResponse): void {
    // This is the single API-to-view mapping boundary. It also resets dirty state,
    // pending deletions, and the exit snapshot to the newly loaded backend state.
    this.cartStatus.set(data.cartStatus ?? '');
    this.displayedRejectionReason.set((data.rejectionReason ?? '').trim());
    this.displayedRejectionComment.set((data.rejectionComment ?? '').trim());
    this.pageTitle.set(data.page?.title);
    this.pageSubtitle.set(data.page?.subTitle);
    this.mandatoryNote.set(data.page?.mandatoryNote);

    if (data.alerts?.length > 0) {
      this.showInlineWarning.set(true);
      this.alertTitle.set(data.alerts[0].title);
      this.alertMessage.set(data.alerts[0].message);
    } else {
      this.showInlineWarning.set(false);
    }
    // remove undefind and keep it blank if no data in responce 
    const buildAccount = (id?: string, name?: string) =>
      [id, name].filter(Boolean).join(' - ');

    this.accountCards.set([
      { title: 'Ship To', account: buildAccount(data?.account?.shipTo?.id, data?.account?.shipTo?.name), address: data?.account?.shipTo?.address ?? '', accountType: 'SHIP_TO' },
      { title: 'Sold To', account: buildAccount(data?.account?.soldTo?.id, data?.account?.soldTo?.name), address: data?.account?.soldTo?.address ?? '', accountType: 'SOLD_TO' },
    ]);

    const od = data?.orderDetails;
    this.orderCountry.set((data?.loginUserCountryCode ?? '').trim().toUpperCase());
    if (od?.orderType?.options?.length) {
      this.orderTypeOptions.set(od.orderType.options);
    }
    this.shippingMethodOptions.set(['', ...(od?.shippingMethod?.options ?? []).filter(option => option !== '')]);
    this.incotermOptions.set(['', ...(od?.incoterms?.options ?? []).filter(option => option !== '')]);
    this.form.patchValue({
      orderType: this.resolveOrderTypeOption(od?.orderType?.value),
      purchaseOrder: od?.purchaseOrder ?? '',
      shippingMethod: od?.shippingMethod?.value ?? '',
      incoterms: od?.incoterms?.value ?? '',
      salesText: od?.internalSalesText ?? '',
      deliveryNoteText: od?.deliveryNoteText ?? '',
      comment: od?.comments ?? '',
      shipToId: data?.account?.shipTo?.id ?? '',
      soldToId: data?.account?.soldTo?.id ?? '',
    });
    this.headerShipTo.set(data?.account?.shipTo?.id ?? '');
    this.shipToName = data?.account?.shipTo?.name ?? '';
    this.shipToAddress = data?.account?.shipTo?.address ?? '';
    this.soldToName = data?.account?.soldTo?.name ?? '';
    this.soldToAddress = data?.account?.soldTo?.address ?? '';

    this.dirtyRowIds.clear();
    this.pendingDeleteRows = [];
    this.originalRowsById.clear();

    if (data.entries?.length) {
      const mappedRows = data.entries.map((entry, index) => ({
        id: index + 1,
        cartEntryId: entry.cartEntryId ?? -1,
        material: entry.materialNumber ?? '',
        serialNumber: entry.serialNumber ?? '',
        invoiceText: entry.invoiceText ?? '',
        deliveryNoteText: entry.deliveryNoteText ?? '',
        quantity: entry.quantity,
        orderType: this.resolveOrderTypeOption(entry.orderType),
        shipTo: entry.shipTo,
        serialStatus: entry.serialSystemStatus ?? '',
        userStatus: entry.serialUserStatus ?? '',
        isWarning: false,
        isShipToConflict: false,
        isNew: false,
      }));
      this.rows.set(mappedRows);
      mappedRows.forEach((row) => this.originalRowsById.set(row.id, { ...row }));
      this.currentPage.set(1);
    } else {
      this.rows.set([]);
    }

    this.syncHeaderOrderTypeForLineItems();

    // COMPLETED/REJECTED carts are locked: the header form is disabled after the
    // above sync so this always wins over the order-type enable/disable logic.
    if (this.isReadOnly()) {
      this.form.disable({ emitEvent: false });
    }

    // Baseline snapshot to detect unsaved changes on exit. The snapshot is taken
    // only after account display fields and header controls have been populated.
    this.initialOrderPayloadSnapshot = JSON.stringify(this.buildOrderPayload());
  }

  /** Applies the PDF attachments from the review response; non-PDF entries are ignored. */
  private applyReviewAttachments(source?: ReviewOrderAttachment[]): void {
    this.pdfError.set('');
    const pdfAttachments = (source ?? []).filter(
      attachment => attachment?.fileType === 'application/pdf' && !!attachment?.downloadUrl
    );
    // Email attachments are listed first so the dropdown and viewer default to them; other order is preserved.
    const isEmail = (attachment: ReviewOrderAttachment) => labelForAttachment(attachment.fileName) === 'Email';
    const attachments = [...pdfAttachments.filter(isEmail), ...pdfAttachments.filter(a => !isEmail(a))];
    this.pdfDocuments.set(attachments);
    if (attachments.length) {
      this.selectPdfDocument(attachments[0]);
    } else {
      this.clearPdfSource();
      this.isPdfLoading.set(false);
    }
  }

  /** Fetches the attachment through HttpClient and hands the bytes to the viewer. */
  private selectPdfDocument(attachment: ReviewOrderAttachment): void {
    // Cancel the previous download and ignore stale responses when the user changes
    // documents quickly; only the currently selected URL may update the viewer.
    this.isPdfLoading.set(true);
    this.pdfError.set('');
    this.pdfPage.set(1);
    this.pdfTotalPages.set(0);
    this.pdfSrc.set(null);
    const url = this.buildPdfUrl(attachment);
    this.pdfUrl.set(url);

    if (this.pdfSubscription) {
      this.pdfSubscription.unsubscribe();
    }

    this.pdfSubscription = this.pagesService.getAttachmentBlob(attachment.downloadUrl).subscribe({
      next: blob => {
        if (this.pdfUrl() !== url) {
          return;
        }
        this.pdfSrc.set(blob);
        this.isPdfLoading.set(false);
      },
      error: error => {
        if (this.pdfUrl() !== url) {
          return;
        }
        console.error('Failed to load PDF attachment:', error);
        this.pdfSrc.set(null);
        this.pdfError.set('Unable to display this document. Please try again.');
        this.isPdfLoading.set(false);
      },
    });
  }

  private clearPdfSource(): void {
    this.pdfSrc.set(null);
    this.pdfUrl.set('');
    this.pdfPage.set(0);
    this.pdfTotalPages.set(0);
  }

  private buildPdfUrl(attachment: ReviewOrderAttachment): string {
    const base = environment.baseUrl.replace(/\/$/, '');
    return attachment.downloadUrl.startsWith('http')
      ? attachment.downloadUrl
      : `${base}/${attachment.downloadUrl.replace(/^\//, '')}`;
  }

  /** Updates pdfTotalPages when the PDF finishes loading. */
  onPdfPagesLoaded(event: PagesLoadedEvent): void {
    this.pdfTotalPages.set(event.pagesCount);
  }

  /** Sets the displayed PDF page within 1–pdfTotalPages; out-of-range values are ignored. */
  setPdfPage(page: number): void {
    if (page < 1 || page > this.pdfTotalPages()) {
      return;
    }
    this.pdfPage.set(page);
  }

  /** Decrements the PDF page if not already on page 1. */
  previousPdfPage(): void {
    if (this.pdfPage() > 1) {
      this.pdfPage.update(p => p - 1);
    }
  }

  /** Increments the PDF page if not already on the last page. */
  nextPdfPage(): void {
    if (this.pdfPage() < this.pdfTotalPages()) {
      this.pdfPage.update(p => p + 1);
    }
  }

  /** Rotates the PDF iframe 90° clockwise on each call, cycling through 0/90/180/270. */
  rotatePdf(): void {
    this.pdfRotation.update(r => (r + 90) % 360);
  }

  /** Switches the viewer to the selected attachment. */
  changePdfDocument(event: Event): void {
    const documentId = (event.target as HTMLSelectElement).value;
    const attachment = this.pdfDocuments().find(doc => doc.documentId === documentId);
    if (!attachment) {
      return;
    }
    this.selectPdfDocument(attachment);
  }

  /** Opens the current PDF URL in a new browser tab with noopener/noreferrer for security. */
  openPdfInNewTab(): void {
    const url = this.pdfUrl();
    if (!url) {
      return;
    }
    window.open(url.replace('&attachment=true', ''), '_blank', 'noopener,noreferrer');
  }

  /** Adjusts zoom by delta percentage, clamped to the range 60–180%. */
  changeZoom(delta: number): void {
    const nextZoom = Math.max(20, Math.min(200, this.pdfZoom() + delta));
    this.pdfZoom.set(nextZoom);
  }

  /** Returns true once a required field has been interacted with and is still invalid. */
  isFieldInvalid(field: 'orderType' | 'purchaseOrder' | 'shippingMethod'): boolean {
    const control = this.form.controls[field];
    return control.invalid && (control.touched || control.dirty);
  }
}
